/**
 * `<SocraticActions>` — the Tutor UI's only request surface.
 *
 * The actions come from `@teacher-ai-hub/shared#SOCRATIC_ACTIONS`, a
 * closed enum. There is intentionally no free-text prompt input on the
 * Tutor page in the MVP — the safety guarantee in S1-012 is that the
 * only requests a student can send to the (yet-to-ship) tutor agent are
 * the four learning-safe ones the design doc approves.
 *
 * The component is a pure UI shell: pressing a button calls back into
 * the parent with the chosen action id. The actual agent call lands in
 * a later sprint.
 */

import { SOCRATIC_ACTIONS, type SocraticActionId } from '@teacher-ai-hub/shared';

export interface SocraticActionsProps {
  /** Currently armed action, if any. */
  selectedId?: SocraticActionId | null;
  onSelect(id: SocraticActionId): void;
  /** Disable every button — used while a (mock) request is pending. */
  disabled?: boolean;
}

export function SocraticActions({
  selectedId = null,
  onSelect,
  disabled = false,
}: SocraticActionsProps) {
  return (
    <div
      className="socratic-actions"
      role="group"
      aria-label="Learning-safe tutor actions"
      data-testid="socratic-actions"
    >
      {SOCRATIC_ACTIONS.map((action) => {
        const isSelected = action.id === selectedId;
        return (
          <button
            key={action.id}
            type="button"
            className={
              isSelected
                ? 'btn btn--primary socratic-actions__btn is-selected'
                : 'btn socratic-actions__btn'
            }
            onClick={() => onSelect(action.id)}
            disabled={disabled}
            aria-pressed={isSelected}
            aria-describedby={`socratic-${action.id}-desc`}
            data-testid={`socratic-action-${action.id}`}
          >
            <span className="socratic-actions__label">{action.label}</span>
            <span
              id={`socratic-${action.id}-desc`}
              className="socratic-actions__desc"
            >
              {action.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
